import { useMemo, useState } from 'react';
import { Outlet, useLocation, useNavigate, useSearchParams } from 'react-router';
import { useAuth } from '@/context/AuthContext';
import { useExpenses } from '@/hooks/useExpenses';
import { Icons } from '@/components/icons';

interface PageMeta {
  crumb: string;
  title: string;
}

function pageMeta(pathname: string): PageMeta {
  if (pathname.startsWith('/dashboard')) return { crumb: 'Workspace', title: 'Dashboard' };
  if (pathname.startsWith('/expenses/new')) return { crumb: 'Workspace · Receipts', title: 'New receipt' };
  if (/^\/expenses\/[^/]+\/edit/.test(pathname)) return { crumb: 'Workspace · Receipts', title: 'Edit receipt' };
  if (pathname.startsWith('/expenses')) return { crumb: 'Workspace', title: 'Receipts' };
  if (pathname.startsWith('/reports')) return { crumb: 'Workspace', title: 'Reports' };
  if (pathname.startsWith('/admin/users')) return { crumb: 'Admin', title: 'Team & access' };
  return { crumb: 'Workspace', title: 'Dashboard' };
}

export function AppLayout() {
  const { user, isAdmin, signOut } = useAuth();
  const { expenses } = useExpenses();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [navOpen, setNavOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  const counts = useMemo(
    () => ({
      total: expenses.length,
      pending: expenses.filter((e) => e.status === 'pending').length,
      reimbursed: expenses.filter((e) => e.status === 'reimbursed').length,
      donated: expenses.filter((e) => e.status === 'donation').length,
    }),
    [expenses],
  );

  const { pathname } = location;
  const statusParam = searchParams.get('status');
  const meta = pageMeta(pathname);

  const onExpenses = pathname.startsWith('/expenses') && !pathname.startsWith('/expenses/new');
  const isReceiptsActive = onExpenses && !/\/edit$/.test(pathname) && (!statusParam || statusParam === 'all');

  const go = (to: string) => {
    setNavOpen(false);
    navigate(to);
  };

  const handleSignOut = async () => {
    setSigningOut(true);
    await signOut();
    setSigningOut(false);
    navigate('/login', { replace: true });
  };

  const displayName = user?.displayName || user?.email || 'Account';
  const initials =
    displayName
      .split(/\s+/)
      .map((s) => s[0])
      .filter(Boolean)
      .slice(0, 2)
      .join('')
      .toUpperCase() || 'CL';

  const navItems = [
    { key: 'dashboard', label: 'Dashboard', icon: Icons.Dashboard, to: '/dashboard', active: pathname.startsWith('/dashboard') },
    { key: 'receipts', label: 'Receipts', icon: Icons.Receipt, to: '/expenses', active: isReceiptsActive, count: counts.total },
    { key: 'add', label: 'Add receipt', icon: Icons.Plus, to: '/expenses/new', active: pathname.startsWith('/expenses/new') },
    { key: 'reports', label: 'Reports', icon: Icons.Chart, to: '/reports', active: pathname.startsWith('/reports') },
  ];

  const statusItems = [
    { key: 'pending', label: 'Pending', color: 'var(--pending)', count: counts.pending },
    { key: 'reimbursed', label: 'Reimbursed', color: 'var(--reimbursed)', count: counts.reimbursed },
    { key: 'donation', label: 'Donated', color: 'var(--donated)', count: counts.donated },
  ];

  return (
    <div className={`app ${navOpen ? 'nav-open' : ''}`}>
      <div className="nav-backdrop" onClick={() => setNavOpen(false)} />

      <aside className="sidebar">
        <div className="brand">
          <div className="mark">☕</div>
          <div>
            <div className="name">Café Ledger</div>
            <div className="sub">Café · Ops</div>
          </div>
          <button className="drawer-close" onClick={() => setNavOpen(false)} title="Close menu" aria-label="Close menu">
            <Icons.X size={18} />
          </button>
        </div>

        <div className="nav-section">
          <div className="nav-label">Workspace</div>
          {navItems.map((it) => {
            const Ic = it.icon;
            return (
              <button key={it.key} className={`nav-item ${it.active ? 'active' : ''}`} onClick={() => go(it.to)}>
                <Ic className="ico" />
                <span>{it.label}</span>
                {it.count != null && <span className="badge">{it.count}</span>}
              </button>
            );
          })}
        </div>

        <div className="nav-section">
          <div className="nav-label">Status</div>
          {statusItems.map((s) => (
            <button
              key={s.key}
              className={`nav-item ${onExpenses && statusParam === s.key ? 'active' : ''}`}
              onClick={() => go(`/expenses?status=${s.key}`)}
            >
              <span
                className="ico"
                style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: s.color, marginLeft: 4 }}
              />
              <span>{s.label}</span>
              <span className="badge">{s.count}</span>
            </button>
          ))}
        </div>

        {isAdmin && (
          <div className="nav-section">
            <div className="nav-label">Admin</div>
            <button className={`nav-item ${pathname.startsWith('/admin/users') ? 'active' : ''}`} onClick={() => go('/admin/users')}>
              <Icons.Users className="ico" />
              <span>Team &amp; access</span>
            </button>
          </div>
        )}

        <div className="user-card">
          <div className="avatar">{initials}</div>
          <div className="who">
            <div className="uname">{user?.displayName || 'Account'}</div>
            <div className="uemail">{user?.email}</div>
          </div>
          <button className="signout" title="Sign out" onClick={handleSignOut} disabled={signingOut}>
            <Icons.LogOut size={15} />
          </button>
        </div>
      </aside>

      <main className="main">
        <div className="topbar">
          <button className="menu-btn" onClick={() => setNavOpen(true)} title="Menu" aria-label="Open menu">
            <Icons.Menu size={18} />
          </button>
          <div className="title">
            <div className="crumb">{meta.crumb}</div>
            <h1>{meta.title}</h1>
          </div>
          <div className="spacer" />
          <div className="search">
            <Icons.Search size={14} stroke="var(--faint)" />
            <input
              placeholder="Search receipts, stores, items…"
              defaultValue={searchParams.get('q') ?? ''}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  const q = (e.target as HTMLInputElement).value.trim();
                  navigate(q ? `/expenses?q=${encodeURIComponent(q)}` : '/expenses');
                }
              }}
            />
            <span className="kbd">⏎</span>
          </div>
          {!pathname.startsWith('/admin/users') && (
            <button className="btn primary" onClick={() => navigate('/expenses/new')}>
              <Icons.Plus size={14} /> Add receipt
            </button>
          )}
        </div>

        <div className="content">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
